import { Clear } from "@mui/icons-material";
import CloseIcon from "@mui/icons-material/Close";
import {
  Autocomplete,
  Box,
  Button,
  createFilterOptions,
  Dialog,
  DialogActions,
  Divider,
  IconButton,
  InputAdornment,
  ListItemText,
  TextField,
  Typography,
} from "@mui/material";
import { DesktopDatePicker, LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import React, { useState } from "react";
import { toast } from "react-toastify";
import LoadingIcon from "src/Components/Common/LoadingIcon";
import useFetch from "src/Components/Common/useFetch";
import CustomToast from "src/Components/CustomToast";
import { API_ENDPOINTS } from "src/Utils/ApiConstants/ApiUrlConstants";
import { isOverFlowing } from "src/Utils/Common/CheckTextOverflow";
import {
  uiLoggerNameDisputeResolution,
  uiLoggerNamesTaskBoard,
  uiLoggerNamesWorkflow,
} from "src/Utils/Recon/UiLogger/Constants";
import uiLogger from "src/Utils/UiLogger";
import { TICKET_PRIORITIES } from "./CreateTicket";

const filter = createFilterOptions<any>();

export default function EditTaskModal({
  type,
  open,
  setOpen,
  row,
  allUsers,
  isEditing,
  setIsEditing,
  fetchRowsData,
  companyId,
  branchCode,
  ...props
}: {
  type: "workflow" | "disputeResolution" | "taskBoard";
  [key: string]: any;
}) {
  const [dueDateErrorText, setDueDateErrorText] = useState("");

  const defaultMaker = allUsers.filter((user) => user.id === row.makerId) || [];
  const defaultChecker = allUsers.filter((user) => user.id === row.checkerId) || [];
  const defaultApprover = allUsers.filter((user) => user.id === row.approverId) || [];

  const [newMaker, setNewMaker] = useState(defaultMaker.length > 0 ? defaultMaker[0] : null);
  const [newChecker, setNewChecker] = useState(defaultChecker.length > 0 ? defaultChecker[0] : null);
  const [newApprover, setNewApprover] = useState(defaultApprover.length > 0 ? defaultApprover[0] : null);
  const [newDueDate, setNewDueDate] = useState(row.dueDate ? new Date(row.dueDate) : null);
  const [newPriority, setNewPriority] = useState(row.priority);
  const [remarks, setRemarks] = useState(row.remarks);
  const [taskLabel, setTaskLabel] = useState<any>(
    type === "workflow" || type === "taskBoard" ? (row.taskLabel ? row.taskLabel : null) : ""
  );

  const updateTaskDetails = () => {
    const payloadObj = { ticketId: row.id || row.ticketId || row.taskId, mappingId: row.mappingId };

    let isAnyFieldUpdated = false;
    if (newMaker && newMaker.id !== row.makerId) {
      isAnyFieldUpdated = true;
      Object.assign(payloadObj, { makerId: newMaker.id });
    }
    if (newChecker && newChecker.id !== row.checkerId) {
      isAnyFieldUpdated = true;
      Object.assign(payloadObj, { checkerId: newChecker.id });
    }
    if (newApprover && newApprover.id !== row.approverId) {
      isAnyFieldUpdated = true;
      Object.assign(payloadObj, { approverId: newApprover.id });
    }
    if (newPriority !== row.priority) {
      isAnyFieldUpdated = true;
      Object.assign(payloadObj, { priority: newPriority });
    }
    const oldDueDate = row.dueDate ? new Date(row.dueDate) : null;

    if (oldDueDate?.getTime() !== newDueDate?.getTime()) {
      isAnyFieldUpdated = true;
      Object.assign(payloadObj, { dueDate: newDueDate });
    }
    let newRemarks = remarks?.trim();
    if (newRemarks === "") newRemarks = null;
    if (newRemarks !== row.remarks) {
      isAnyFieldUpdated = true;
      Object.assign(payloadObj, { remarks: newRemarks });
    }
    if (type === "workflow" || type === "taskBoard") {
      const taskLabelValue = taskLabel ? (taskLabel.title ? taskLabel.title.trim() : taskLabel) : null;

      if (taskLabelValue) {
        if (taskLabelValue.length > 30) {
          toast.error(<CustomToast message="Character limit exceeded. Please add less than 30 characters." />);
          return;
        }
        if (taskLabelValue.includes(",")) {
          toast.error(<CustomToast message="Multiple labels are not allowed for a task." />);
          return;
        }
      }

      if (row.taskLabel !== taskLabelValue) {
        isAnyFieldUpdated = true;
        Object.assign(payloadObj, { taskLabel: taskLabelValue });
      }
    }
    if (!isAnyFieldUpdated) {
      setOpen(false);
      return;
    }
    if (type === "workflow") {
      uiLogger(uiLoggerNamesWorkflow.UI_WF_DB_ACTIONS_EDIT_SAVE_CLICK.functionName, companyId, branchCode, {
        message: uiLoggerNamesWorkflow.UI_WF_DB_ACTIONS_EDIT_SAVE_CLICK.message,
        idOfTicket: row.id,
      });
    }
    if (type === "disputeResolution") {
      uiLogger(uiLoggerNameDisputeResolution.UI_DR_CT_EditTask_Save_Click.functionName, companyId, branchCode, {
        message: uiLoggerNameDisputeResolution.UI_DR_CT_EditTask_Save_Click.message,
      });
    }
    if (type === "taskBoard") {
      uiLogger(uiLoggerNamesTaskBoard.UI_WF_TB_ACTIONS_EDIT_SAVE_CLICK.functionName, companyId, branchCode, {
        message: uiLoggerNamesTaskBoard.UI_WF_TB_ACTIONS_EDIT_SAVE_CLICK.message,
        idOfTicket: row.taskId,
      });
    }

    setIsEditing(true);

    useFetch(API_ENDPOINTS.UPDATE_TICKET.url, "POST", {
      failureMessage: API_ENDPOINTS.UPDATE_TICKET.failureMessage,
      data: payloadObj,
      thenCallBack: () => {
        setIsEditing(false);
        setOpen(false);
        if (type === "workflow" || type === "taskBoard") props.getAllTaskLabels();
        fetchRowsData();
        toast.success(<CustomToast message="Task updated successfully" />);
      },
      catchCallBack: () => {
        setIsEditing(false);
        setOpen(false);
        if (type === "workflow" || type === "taskBoard") props.getAllTaskLabels();
        fetchRowsData();
      },
    });
  };
  return (
    <Dialog open={open} sx={{ "& .MuiDialog-paper": { borderRadius: "4px", width: "500px" } }}>
      <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: 2 }}>
        <Typography
          ref={(th) => {
            if (th && isOverFlowing(th)) th.title = th.innerText;
          }}
          className="textOverflow_hidden"
          variant="h6"
        >
          Edit task for {row.businessPartnerName || row.businessPartner}
        </Typography>
        <IconButton
          onClick={() => {
            setOpen(false);
          }}
        >
          <CloseIcon />
        </IconButton>
      </Box>
      <Divider />
      <Box sx={{ display: "flex", flexDirection: "column", gap: 2, padding: 2 }}>
        <Autocomplete
          onChange={(_, newValue) => {
            setNewMaker(newValue);
          }}
          value={newMaker}
          disableClearable
          options={allUsers}
          renderOption={(props, item) => (
            <li {...props} key={item.id}>
              <ListItemText>{item.name}</ListItemText>
            </li>
          )}
          getOptionLabel={(option: { name: string; id: number }) => option.name}
          renderInput={(params) => <TextField {...params} label="Maker" variant="standard" />}
        />
        <Autocomplete
          onChange={(_, newValue) => {
            setNewChecker(newValue);
          }}
          value={newChecker}
          disableClearable
          getOptionLabel={(option: { name: string; id: number }) => option.name}
          options={allUsers}
          renderOption={(props, item) => (
            <li {...props} key={item.id}>
              <ListItemText>{item.name}</ListItemText>
            </li>
          )}
          renderInput={(params) => <TextField {...params} label="Checker" variant="standard" />}
        />
        <Autocomplete
          onChange={(_, newValue) => {
            setNewApprover(newValue);
          }}
          disableClearable
          value={newApprover}
          getOptionLabel={(option: { name: string; id: number }) => option.name}
          options={allUsers}
          renderOption={(props, item) => (
            <li {...props} key={item.id}>
              <ListItemText>{item.name}</ListItemText>
            </li>
          )}
          renderInput={(params) => <TextField {...params} label="Approver" variant="standard" />}
        />
        <Autocomplete
          onChange={(_, newValue) => {
            setNewPriority(newValue);
          }}
          disableClearable
          value={newPriority}
          options={TICKET_PRIORITIES}
          renderInput={(params) => <TextField {...params} label="Priority" variant="standard" />}
        />
        <LocalizationProvider dateAdapter={AdapterDateFns}>
          <DesktopDatePicker
            label="Due Date"
            value={newDueDate}
            onError={(error) => {
              if (error) setDueDateErrorText(error);
              else setDueDateErrorText("");
            }}
            onChange={(date: Date) => {
              setNewDueDate(date);
            }}
            format="dd/MM/yyyy"
            slotProps={{
              textField: {
                helperText: dueDateErrorText && "Invalid Date",
                variant: "standard",
                InputProps: {
                  startAdornment: (
                    <InputAdornment position="start" sx={{ margin: 0 }}>
                      {newDueDate && (
                        <IconButton
                          size="small"
                          sx={{ position: "absolute", right: "30px" }}
                          onClick={() => {
                            setNewDueDate(null);
                          }}
                        >
                          <Clear />
                        </IconButton>
                      )}
                    </InputAdornment>
                  ),
                },
              },
            }}
          />
        </LocalizationProvider>
        <TextField
          label="Remarks"
          onChange={(event) => {
            setRemarks(event.target.value);
          }}
          value={remarks}
          multiline
          maxRows={4}
          variant="standard"
        />
        {(type === "workflow" || type === "taskBoard") && (
          <Autocomplete
            sx={{ "& fieldset": { borderRadius: "4px" }, width: "100%" }}
            value={taskLabel}
            onChange={async (_, newValue) => {
              if (typeof newValue === "string") {
                setTaskLabel({
                  title: newValue,
                });
              } else if (newValue && newValue.inputValue) {
                // Create a new value from the user input
                setTaskLabel({
                  title: newValue.inputValue,
                });
              } else {
                setTaskLabel(newValue);
              }
            }}
            filterOptions={(options, params) => {
              const filtered = filter(options, params);

              const { inputValue } = params;
              // Suggest the creation of a new value
              if (inputValue.trim().length === 0) {
                return filtered;
              }
              const isExisting = options.some((option) => inputValue.toLowerCase() === option.title.toLowerCase());
              if (inputValue !== "" && !isExisting) {
                filtered.push({
                  inputValue,
                  title: `Add "${inputValue}"`,
                });
              }

              return filtered;
            }}
            selectOnFocus
            clearOnBlur
            handleHomeEndKeys
            options={props.allTaskLabels}
            getOptionLabel={(option) => {
              // Value selected with enter, right from the input
              if (typeof option === "string") {
                return option;
              }
              if (option.inputValue) {
                return option.inputValue;
              }
              // Regular option
              return option.title;
            }}
            renderOption={(props, option) => {
              const { ...optionProps } = props;
              return (
                <li key={option.title} {...optionProps}>
                  {option.title}
                </li>
              );
            }}
            freeSolo
            renderInput={(params) => <TextField {...params} label="Task Label" size="small" variant="standard" />}
          />
        )}
      </Box>

      <Divider />
      <DialogActions sx={{ padding: 2, background: "#F1F1F1" }}>
        <Button
          disabled={isEditing}
          variant="contained"
          sx={{
            background: "#27B27C",
            padding: "8px 22px",
            color: "#FFF",
            borderRadius: "4px !important",
            fontWeight: 500,
            fontSize: "15px",
            "&:hover, &.Mui-focusVisible": { background: "#27B27C" },
          }}
          onClick={() => {
            updateTaskDetails();
          }}
        >
          <LoadingIcon loading={isEditing} />
          Save
        </Button>
      </DialogActions>
    </Dialog>
  );
}
